// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import GamePreview from './GaragePreview';
import Game3D from './Game3D';
import './App.css';
import { GameEvents } from './constants';
import { initializeGame } from './game/Main';
import { getSocketConfig } from './utils/socketUrl';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useNavigate,
  Navigate,
  useParams
} from 'react-router-dom';

const skinOptions = [
  { name: 'T90A', src: '/assets/T90A.png' },
  { name: 'T90B', src: '/assets/T90B.png' },
  { name: 'T90C', src: '/assets/T90C.png' },
  { name: 'T90D', src: '/assets/T90D.png' },
  { name: 'T90E', src: '/assets/T90E.png' },
  { name: 'T90F', src: '/assets/T90F.png' },
];

function useUsernameController(socket, selectedSkin) {
  const [username, setUsername] = useState(sessionStorage.getItem("username") || "");
  const [hasJoined, setHasJoined] = useState(false);
  
  useEffect(() => {
    if (username && socket && !hasJoined) {
      sessionStorage.setItem("username", username);
      if (selectedSkin) {
        sessionStorage.setItem("selectedSkin", selectedSkin);
      }
      socket.emit(GameEvents.PLAYER_JOIN, username, selectedSkin);
      setHasJoined(true);
    }
  }, [username, socket, selectedSkin, hasJoined]);
  
  // Handle skin changes separately
  useEffect(() => {
    if (username && socket && hasJoined) {
      socket.emit(GameEvents.PLAYER_UPDATE, { color: selectedSkin });
    }
  }, [selectedSkin, socket, username, hasJoined]);
  
  return [username, setUsername];
}

function SkinSelector({ selectedSkin, onSelectSkin }) {
  const currentIndex = skinOptions.findIndex(skin => skin.name === selectedSkin);



  const handlePrevious = () => {
    const newIndex = currentIndex > 0 ? currentIndex - 1 : skinOptions.length - 1;
    onSelectSkin(skinOptions[newIndex].name);
  };

  const handleNext = () => {
    const newIndex = currentIndex < skinOptions.length - 1 ? currentIndex + 1 : 0;
    onSelectSkin(skinOptions[newIndex].name);
  };

  return (
    <div className="skin-selector">
      <div className="skin-navigation">
        <button className="nav-arrow nav-arrow-left" onClick={handlePrevious}>
          ‹
        </button>
        <div className="current-skin">
          <img src={skinOptions[currentIndex]?.src} alt={selectedSkin} />
        </div>
        <button className="nav-arrow nav-arrow-right" onClick={handleNext}>
          ›
        </button>
      </div>
      <p className="selectedSkin hide-on-small">{selectedSkin}</p>
    </div>
  );
}

function RoomPanel({ socket, username, onGarage, onJoinGame }) {
  const [rooms, setRooms] = useState([]);
  const [publicRoomInfo, setPublicRoomInfo] = useState({ room: null, playerCount: 0 });
  const [showCreateInput, setShowCreateInput] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');

  useEffect(() => {
    if (!socket) return;

    // Request initial rooms data
    socket.emit(GameEvents.ROOMS_REQUEST);
    socket.emit('public_room_request');

    socket.on(GameEvents.ROOMS_UPDATE, (updatedRooms) => {
      setRooms(updatedRooms);
    });

    socket.on('public_room_update', (info) => {
      setPublicRoomInfo(info);
    });

    return () => {
      socket.off(GameEvents.ROOMS_UPDATE);
      socket.off('public_room_update');
    };
  }, [socket]);

  const handleJoinRoom = async (roomId) => {
    if (!socket) return;
    console.log(`[${new Date().toISOString()}] RoomPanel: Joining room:`, roomId);
    try {
      let resp;
      if (typeof socket.emitWithAck === 'function') {
        resp = await socket.emitWithAck(GameEvents.ROOM_JOIN, roomId);
      } else {
        resp = await new Promise((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('timeout')), 5000);
          socket.emit(GameEvents.ROOM_JOIN, roomId, (r) => { clearTimeout(t); resolve(r); });
        });
      }
      if (!resp || !resp.ok) {
        console.warn('Room join failed', resp?.error);
      }
    } catch (e) {
      console.warn('Room join failed', e);
    }
  };

  const handleCreateRoom = async () => {
    if (!(socket && newRoomName.trim())) return;
    try {
      let resp;
      if (typeof socket.emitWithAck === 'function') {
        resp = await socket.emitWithAck(GameEvents.ROOM_CREATE, newRoomName.trim());
      } else {
        resp = await new Promise((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('timeout')), 5000);
          socket.emit(GameEvents.ROOM_CREATE, newRoomName.trim(), (r) => { clearTimeout(t); resolve(r); });
        });
      }
      if (resp && resp.ok) {
        setNewRoomName('');
        setShowCreateInput(false);
      } else {
        console.warn('Room create failed', resp?.error);
      }
    } catch (e) {
      console.warn('Room create failed', e);
    }
  };

  const toggleCreateInput = () => {
    setShowCreateInput(!showCreateInput);
    if (showCreateInput) {
      setNewRoomName('');
    }
  };


  const handleGarage = () => {
    if (onGarage) {
      onGarage();
    }
  };

  const handleJoinRoomGarage = async (roomId) => {
    if (!socket) return;
    console.log(`[${new Date().toISOString()}] RoomPanel: Joining room garage:`, roomId);
    try {
      let resp;
      if (typeof socket.emitWithAck === 'function') {
        resp = await socket.emitWithAck(GameEvents.ROOM_JOIN, roomId);
      } else {
        resp = await new Promise((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('timeout')), 5000);
          socket.emit(GameEvents.ROOM_JOIN, roomId, (r) => { clearTimeout(t); resolve(r); });
        });
      }
      if (!resp || !resp.ok) {
        console.warn('Room join (garage) failed', resp?.error);
      }
    } catch (e) {
      console.warn('Room join (garage) failed', e);
    }
    // Navigate to room garage regardless; UI flow keeps user moving
    if (onJoinGame) onJoinGame(roomId);
  };

  const handleJoinGame = (roomId) => {
    if (onJoinGame) {
      onJoinGame(roomId);
    }
  };

  return (
    <div className="panel">
      
      {/* Epic Public Room Button */}
      <div className="epic-public-room-container">
        <button
          onClick={handleGarage}
          className="epic-public-room-button"
        >
          <span className="epic-button-text">Public Room ({publicRoomInfo.playerCount} players)</span>
        </button>
      </div>
      <div className="room-panel-header">
        <h2>Roomky: {rooms.length}</h2>
        <button
          onClick={toggleCreateInput}
          className="create-room-toggle-button"
        >
          {showCreateInput ? '×' : '+'}
        </button>
      </div>


      {showCreateInput && (
        <div className="room-creation-container">
          <input
            type="text"
            placeholder="Název místnosti..."
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreateRoom()}
            className="room-creation-input"
          />
          <button
            onClick={handleCreateRoom}
            className="room-creation-button"
          >
            Vytvořit
          </button>
        </div>
      )}

      {rooms.map(room => (
        <div key={room.id} className="room-list-item">
          <div className="room-info">
            <span className="room-name">{room.name}</span>
            <span className="room-player-count">({room.memberIds?.length || 0} players)</span>
          </div>
          <button
            onClick={() => handleJoinRoomGarage(room.id)}
            className="big-button"
          >
            Připojit se
          </button>
        </div>
      ))}
    </div>
  );
}

function UsersPanel({ socket, username }) {
  const [users, setUsers] = useState([]);

  useEffect(() => {
    if (!socket) return;

    // Request initial players data
    socket.emit(GameEvents.PLAYERS_REQUEST);

    socket.on(GameEvents.PLAYERS_UPDATE, (updatedUsers) => {
      setUsers(updatedUsers);
    });

    return () => {
      socket.off(GameEvents.PLAYERS_UPDATE);
    };
  }, [socket]);

  return (
    <div className="panel">
      <h2>Hráč: {username}</h2>
      <h2>Online Hráči: {users.length}</h2>
      {users.map(user => (
        <div key={user.id} className="user-list-item">
          <span>
            {user.name}
          </span>
          <span className={`user-status-badge ${user.roomId ? 'in-room' : 'in-lobby'}`}>
            {user.roomId ? `V místnosti` : `V lobby`}
          </span>
        </div>
      ))}
    </div>
  );
}

function ThemeToggleElement({ theme, toggleTheme }) {
  return (
    <button
      onClick={toggleTheme}
      className="theme-toggle-button"
    >
      {theme === 'dark' ? '☀️ Light' : '🌙 Dark'}
    </button>
  );
}

// Loading Page Component
const LoadingPage = ({ progress = 0, message = "Loading assets..." }) => {
  return (
    <div className="loading-page">
      <div className="loading-content">
        <div className="loading-spinner"></div>
        <h2 className="loading-title">Loading Game</h2>
        <p className="loading-message">{message}</p>
        <div className="loading-progress">
          <div className="loading-progress-bar">
            <div 
              className="loading-progress-fill" 
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <span className="loading-progress-text">{Math.round(progress)}%</span>
        </div>
      </div>
    </div>
  );
};

// Leaving Page Component
const LeavingPage = ({ message = "Leaving game..." }) => {
  return (
    <div className="leaving-page">
      <div className="leaving-content">
        <div className="leaving-spinner"></div>
        <h2 className="leaving-title">Leaving Game</h2>
        <p className="leaving-message">{message}</p>
      </div>
    </div>
  );
};

// Game Page with Loading States
const GamePage = ({ username, selectedSkin, roomId, socket }) => {
  const [gameState, setGameState] = useState('loading'); // 'loading', 'playing', 'leaving'
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const gameRef = useRef(null);
  const containerRef = useRef(null);
  const isInitializingRef = useRef(false);

  useEffect(() => {
    if (gameState === 'loading' && !isInitializingRef.current) {
      isInitializingRef.current = true;
      initializeGameWithLoading();
    }
  }, [gameState]);

  const initializeGameWithLoading = async () => {
    try {
      setLoadingMessage('Connecting to server...');
      setLoadingProgress(10);

      // Initialize game with progress callbacks
      const gameObjects = await initializeGame(
        containerRef.current, 
        username, 
        selectedSkin, 
        roomId,
        (progress, message) => {
          setLoadingProgress(progress);
          setLoadingMessage(message);
        },
        socket
      );

      gameRef.current = gameObjects;
      setGameState('playing');
    } catch (error) {
      console.error('Failed to initialize game:', error);
      isInitializingRef.current = false;
      // Handle error - maybe redirect back to garage
      window.location.href = '/garage';
    }
  };

  const handleLeaveGame = async () => {
    setGameState('leaving');
    
    try {
      // Disconnect from game room
      if (gameRef.current?.game?.multiplayer) {
        gameRef.current.game.multiplayer.removeListeners();
      }
      
      // Unload all assets
      if (window.AssetManager) {
        window.AssetManager.clear();
      }
      
      // Wait a bit for cleanup
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Navigate back to lobby
      window.location.href = '/';
    } catch (error) {
      console.error('Error leaving game:', error);
      window.location.href = '/';
    }
  };

  // Expose leave function globally for death screen
  useEffect(() => {
    window.leaveGame = handleLeaveGame;
    return () => {
      delete window.leaveGame;
    };
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Cleanup when component unmounts
      if (gameRef.current) {
        handleLeaveGame();
      }
    };
  }, []);

  if (gameState === 'loading') {
    return (
      <>
        <div ref={containerRef} style={{ display: 'none' }} />
        <LoadingPage progress={loadingProgress} message={loadingMessage} />
      </>
    );
  }

  if (gameState === 'leaving') {
    return <LeavingPage />;
  }

  return (
    <div
      ref={containerRef}
      style={{
        width: '100vw',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 1000
      }}
    />
  );
};

// Wrapper component to extract roomId from URL params
function Game3DPageWrapper({ username, selectedSkin, socket }) {
  const { roomId } = useParams();
  return (
    <GamePage 
      username={username} 
      selectedSkin={selectedSkin} 
      roomId={roomId} 
      socket={socket}
    />
  );
}

// New RoomGarage component
const RoomGarage = ({ socket, username, selectedSkin, setSelectedSkin, roomId }) => {
  const navigate = useNavigate();
  const [garagePlayers, setGaragePlayers] = useState([]);
  const [isReady, setIsReady] = useState(false);
  const [gameStarting, setGameStarting] = useState(false);

  console.log('RoomGarage: received roomId:', roomId);

  // Redirect to main garage if no roomId
  useEffect(() => {
    if (!roomId) {
      console.log('RoomGarage: No roomId, redirecting to /garage');
      navigate('/garage');
      return;
    }
  }, [roomId, navigate]);

  useEffect(() => {
    if (!socket || !roomId) return;

    // Join garage for this room
    socket.emit(GameEvents.GARAGE_JOIN, roomId);

    // Listen for garage players updates
    const handleGaragePlayersUpdate = (players) => {
      console.log('Garage players update:', players);
      setGaragePlayers(players);
    };

    // Listen for game start
    const handleGameStart = (data) => {
      console.log('Game starting for room:', data.roomId);
      setGameStarting(true);
      // Navigate to game after a short delay
      setTimeout(() => {
        navigate(`/game/${data.roomId}`);
      }, 2000);
    };

    socket.on(GameEvents.GARAGE_PLAYERS_UPDATE, handleGaragePlayersUpdate);
    socket.on(GameEvents.GARAGE_START_GAME, handleGameStart);

    return () => {
      if (socket) {
        socket.emit(GameEvents.GARAGE_LEAVE);
        socket.off(GameEvents.GARAGE_PLAYERS_UPDATE, handleGaragePlayersUpdate);
        socket.off(GameEvents.GARAGE_START_GAME, handleGameStart);
      }
    };
  }, [socket, roomId, navigate]);

  const handleReadyToggle = () => {
    if (!socket || !roomId) return;
    
    if (isReady) {
      socket.emit(GameEvents.GARAGE_UNREADY);
      setIsReady(false);
    } else {
      socket.emit(GameEvents.GARAGE_READY);
      setIsReady(true);
    }
  };

  const handleLeaveGarage = () => {
    if (socket) {
      socket.emit(GameEvents.GARAGE_LEAVE);
    }
    navigate('/');
  };

  // Don't render if no roomId
  if (!roomId) {
    return null;
  }

  const readyPlayers = garagePlayers.filter(p => p.isReady);
  const canStart = garagePlayers.length >= 2 && readyPlayers.length === garagePlayers.length;

  return (
    <div className="garage-container">
      <div className="garage-header">
        <h1>Garage - Room: {roomId}</h1>
        <button className="big-button" onClick={handleLeaveGarage}>
          Leave Garage
        </button>
      </div>

      <div className="garage-content">
        <div className="garage-left">
          <GamePreview
            selectedSkin={selectedSkin}
            username={username}
            players={garagePlayers}
            onSelectSkin={setSelectedSkin}
            SkinSelector={SkinSelector}
          />
        </div>

        <div className="garage-right">
          <div className="players-panel">
            <h2>Players in Garage ({garagePlayers.length})</h2>
            <div className="players-list">
              {garagePlayers.map(player => (
                <div key={player.id} className={`player-item ${player.isReady ? 'ready' : 'not-ready'}`}>
                  <span className="player-name">{player.name}</span>
                  <span className="player-status">
                    {player.isReady ? '✅ Ready' : '⏳ Not Ready'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="ready-panel">
            <h2>Ready Status</h2>
            <p>Ready: {readyPlayers.length}/{garagePlayers.length}</p>
            
            <button 
              className={`ready-button ${isReady ? 'ready' : 'not-ready'}`}
              onClick={handleReadyToggle}
              disabled={gameStarting}
            >
              {isReady ? 'Unready' : 'Ready'}
            </button>

            {canStart && (
              <div className="game-start-notice">
                <p>🎮 All players ready! Game will start automatically...</p>
              </div>
            )}

            {gameStarting && (
              <div className="game-starting">
                <p>🚀 Starting game...</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Wrapper component to handle roomId parameter
function RoomGarageWrapper({ socket, username, selectedSkin, setSelectedSkin }) {
  const { roomId } = useParams();
  console.log('RoomGarageWrapper: roomId from useParams:', roomId);
  
  return (
    <RoomGarage 
      socket={socket}
      username={username}
      selectedSkin={selectedSkin}
      setSelectedSkin={setSelectedSkin}
      roomId={roomId}
    />
  );
}

function Rooms({ socket }) {
  const [rooms, setRooms] = useState([]);
  const [newRoomName, setNewRoomName] = useState('');
  const [users, setUsers] = useState([]);
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!socket) return;
    socket.on(GameEvents.PLAYERS_UPDATE, (updatedUsers) => {
      setUsers(updatedUsers);
    });
    socket.on(GameEvents.ROOMS_UPDATE, (updatedRooms) => {
      console.log('Rooms update received:', updatedRooms);
      setRooms(updatedRooms);
    });
    return () => {
      socket.off(GameEvents.PLAYERS_UPDATE);
      socket.off(GameEvents.ROOMS_UPDATE);
    };
  }, [socket]);
  
  const handleCreateRoom = async () => {
    if (!(socket && newRoomName.trim() !== '')) return;
    try {
      let resp: any;
      if (typeof (socket as any).emitWithAck === 'function') {
        resp = await (socket as any).emitWithAck(GameEvents.ROOM_CREATE, newRoomName.trim());
      } else {
        resp = await new Promise((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('timeout')), 5000);
          (socket as any).emit(GameEvents.ROOM_CREATE, newRoomName.trim(), (r: any) => { clearTimeout(t); resolve(r); });
        });
      }
      if (resp && resp.ok) {
        setNewRoomName('');
      } else {
        console.warn('Room create failed', resp?.error);
      }
    } catch (e) {
      console.warn('Room create failed', e);
    }
  };
  
  const handleJoinRoom = async (roomId) => {
    if (!socket) return;
    try {
      let resp: any;
      if (typeof (socket as any).emitWithAck === 'function') {
        resp = await (socket as any).emitWithAck(GameEvents.ROOM_JOIN, roomId);
      } else {
        resp = await new Promise((resolve, reject) => {
          const t = setTimeout(() => reject(new Error('timeout')), 5000);
          (socket as any).emit(GameEvents.ROOM_JOIN, roomId, (r: any) => { clearTimeout(t); resolve(r); });
        });
      }
      if (!resp || !resp.ok) {
        console.warn('Room join failed', resp?.error);
      }
    } catch (e) {
      console.warn('Room join failed', e);
    }
  };

  return (
    <div className="panel social-panel">
      <div className="server-section">
        <div className="create-room">
          <input
            type="text"
            placeholder="Název roomky"
            value={newRoomName}
            onChange={(e) => setNewRoomName(e.target.value)}
          />
          <button className='big-button' onClick={handleCreateRoom}>Vytvořit</button>
        </div>
        <h2>Roomky: {rooms.length}</h2>
        <ul className="server-list">
          {rooms.map(room => (
            <li key={room.id} onClick={() => handleJoinRoom(room.id)}>
              <span className="room-name">{room.name}</span>
              <span className="room-members">({room.memberIds.length} hráčů)</span>
              <button
                className="big-button skin-selector-margin"
                onClick={e => {
                  e.stopPropagation();
                  navigate(`/garage/${room.id}`);
                }}
              >
                Enter Garage
              </button>
            </li>
          ))}
        </ul>
        <h2>Připojení hráči: {users.length}</h2>
        <ul className="user-list">
          {users.map(user => (
            <li key={user.id}>
              {user.name} {user.roomId ? `( ${user.roomName ?? 'Alone'} )` : ''}
            </li>
          ))}
        </ul>

        {rooms.map(room => {
          const playersInRoom = users.filter(u => u.roomId === room.id);
          return (
            <div key={room.name} className="room">
              <h3>
                {room.name} ({playersInRoom.length})
              </h3>
              <ul>
                {playersInRoom.map(player => (
                  <li key={player.id}>{player.name}</li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// NameElement oddělený na stránku WhoAmI
const WhoAmIPage = ({ setUsername }) => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [volume, setVolume] = useState(0.5); // Default volume
  
  // Load saved volume from localStorage
  useEffect(() => {
    const savedVolume = localStorage.getItem('gameVolume');
    if (savedVolume !== null) {
      console.log("sound", savedVolume)
      setVolume(parseFloat(savedVolume));
    }
  }, []);

  const handleSetUsername = () => {
    console.log('handleSetUsername called with inputValue:', inputValue);
    if (inputValue.trim() !== "") {
      console.log('Input is valid, setting username and navigating');
      setUsername(inputValue.trim());
      // Save volume to localStorage
      localStorage.setItem('gameVolume', volume.toString());
      navigate("/");
    } else {
      console.log('Input is empty, not submitting');
    }
  };

  const handleVolumeChange = (newVolume) => {
    setVolume(newVolume);
    // Save volume to localStorage immediately
    localStorage.setItem('gameVolume', newVolume.toString());
  };

  const handleKeyDown = (e) => {
    console.log('Key pressed:', e.key);
    if (e.key === 'Enter') {
      console.log('Enter key detected, calling handleSetUsername');
      e.preventDefault(); // Prevent default form submission
      handleSetUsername();
    }
  };

  return (
    <div className="loginWrapper">
      <div className="loginContent">
        <h2 className="settings-title">User Settings</h2>
        
        <div className="settings-section">
          <label htmlFor="usernameInput" className="settings-label">Player Name:</label>
          <input
            id="usernameInput"
            type="text"
            placeholder="Enter player name"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="settings-input"
            maxLength="20"
          />
        </div>

        <div className="settings-section">
          <label htmlFor="volumeSlider" className="settings-label">Volume:</label>
          <div className="volume-control-container">
            <input
              id="volumeSlider"
              type="range"
              min="0"
              max="1"
              step="0.01"
              value={volume}
              onChange={(e) => handleVolumeChange(parseFloat(e.target.value))}
              className="volume-slider"
            />
            <span className="volume-value">{Math.round(volume * 100)}%</span>
          </div>
        </div>

        <div className="settings-actions">
          <button 
            className='big-button settings-button' 
            onClick={handleSetUsername}
            disabled={!inputValue.trim()}
          >
            Continue
          </button>
        </div>

        <div className="settings-hint">
          Press Enter to submit
        </div>
      </div>
    </div>
  );
};

// Public Garage Page - Full 3D with right panel only
const PublicGaragePage = ({ socket, username, selectedSkin, setSelectedSkin }) => {
  const navigate = useNavigate();
  const [garagePlayers, setGaragePlayers] = useState([]);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    console.log('PublicGaragePage: Component mounted');
    
    if (!socket) {
      console.log('PublicGaragePage: No socket available');
      return;
    }

    console.log('PublicGaragePage: Socket connected, ID:', socket.id);

    // Join public garage
    socket.emit(GameEvents.GARAGE_JOIN, 'public');

    // Listen for garage players updates
    const handleGaragePlayersUpdate = (players) => {
      console.log('Public garage players update:', players);
      console.log('Players details:', players.map(p => ({ id: p.id, name: p.name, color: p.color, isReady: p.isReady })));
      setGaragePlayers(players);
    };

    socket.on(GameEvents.GARAGE_PLAYERS_UPDATE, handleGaragePlayersUpdate);

    return () => {
      console.log('PublicGaragePage: Component unmounting, cleaning up socket listeners');
      if (socket) {
        socket.emit(GameEvents.GARAGE_LEAVE);
        socket.off(GameEvents.GARAGE_PLAYERS_UPDATE, handleGaragePlayersUpdate);
      }
    };
  }, [socket]);

  const handleLeaveGarage = () => {
    if (socket) {
      socket.emit(GameEvents.GARAGE_LEAVE);
    }
    navigate('/');
  };

  const handleReadyToggle = () => {
    if (!socket) return;
    
    console.log('handleReadyToggle called, current isReady:', isReady);
    
    if (isReady) {
      console.log('Emitting GARAGE_UNREADY');
      socket.emit(GameEvents.GARAGE_UNREADY);
      setIsReady(false);
    } else {
      console.log('Emitting GARAGE_READY');
      socket.emit(GameEvents.GARAGE_READY);
      setIsReady(true);
    }
    
    console.log('handleReadyToggle: isReady will be set to:', !isReady);
  };

  const handleStartGame = () => {
    console.log('Starting game from public garage');
    
    if (!socket) {
      console.log('No socket available for starting game');
      return;
    }
    
    // Emit start game event - backend will handle room creation and player joining
    console.log('Emitting GARAGE_START_GAME event');
    socket.emit(GameEvents.GARAGE_START_GAME);
    
    // Listen for game start
    const handleGameStart = (data) => {
      console.log('Public game started:', data);
      console.log('Navigating to game with room ID:', data.roomId);
      socket.off(GameEvents.GAME_START, handleGameStart);
      
      // Navigate to the game with the public room ID
      navigate('/game/PUBLIC_ROOM');
    };
    
    // Add timeout to detect if backend doesn't respond
    const timeout = setTimeout(() => {
      console.log('Timeout waiting for game start response from backend');
      socket.off(GameEvents.GAME_START, handleGameStart);
    }, 5000);
    
    socket.on(GameEvents.GAME_START, (data) => {
      clearTimeout(timeout);
      handleGameStart(data);
    });
  };

  return (
    <div className="public-garage-container">
      <div className="public-garage-3d">
        <GamePreview
          selectedSkin={selectedSkin}
          username={username}
          players={garagePlayers}
          onSelectSkin={setSelectedSkin}
          onLeave={handleLeaveGarage}
          onContinue={handleStartGame}
          SkinSelector={SkinSelector}
        />
      </div>
    </div>
  );
};




function Chat({ socket, username }) {
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    if (!socket) return;
    socket.on(GameEvents.CHAT_MESSAGE, (msg) => {
      setMessages((prev) => [...prev, msg]);
    });
    return () => {
      socket.off(GameEvents.CHAT_MESSAGE);
    };
  }, [socket]);
  const sendMessage = () => {
    if (socket && message.trim() !== '') {
      socket.emit(GameEvents.CHAT_MESSAGE, { user: username, text: message.trim() });
      setMessage('');
    }
  };
  return (
    <div className="panel chat-panel">
      <div className="chat-messages">
        {messages.map((msg, idx) => (
          <div key={idx} className="chat-message">
            <strong>{msg.user}:</strong> {msg.text}
          </div>
        ))}
      </div>
      <div className="chat-input">
        <input
          type="text"
          placeholder="Napište zprávu..."
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
        />
        <button className='big-button' onClick={sendMessage}>Odeslat</button>
      </div>
    </div>
  );
}

function Navbar({ connectionStatus, username, onHome, onGarage, onLogout, showButtons }) {
  return (
    <div className="navbar hide-on-small">
      <div className="navbar-left">
        <span className="player-name">{username}</span>
        <span className={`connection-status ${connectionStatus.toLowerCase()}`}>
          {connectionStatus}
        </span>
      </div>

      {showButtons && (
        <div className="navbar-right">
          <button className="big-button" onClick={onHome}>Domů</button>
          <button className="big-button" onClick={onGarage}>Garáž</button>
          <button className="big-button" onClick={onLogout}>Odhlásit</button>
        </div>
      )}
    </div>
  );
}

function App() {
  const [connectionStatus, setConnectionStatus] = useState("Disconnected");
  const [socket, setSocket] = useState(null);
  const [selectedSkin, setSelectedSkin] = useState(sessionStorage.getItem("selectedSkin") || "T90A");
  const [theme, setTheme] = useState('dark'); // 'light' or 'dark'
  const [username, setUsername] = useUsernameController(socket, selectedSkin);
  const [players, setPlayers] = useState([]); // Seznam hráčů z roomky
  const [currentRoom, setCurrentRoom] = useState(null);
  const usernameRef = useRef(username);

  // Update ref when username changes
  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  // Debug currentRoom changes
  useEffect(() => {
    console.log(`[${new Date().toISOString()}] currentRoom changed to:`, currentRoom);
  }, [currentRoom]);

  useEffect(() => {
    const { url: socketUrl, options } = getSocketConfig();
    const sock = io(socketUrl, options);
    setSocket(sock);
    sock.on("connect", () => {
      setConnectionStatus("Connected");
      console.log(`[${new Date().toISOString()}] Connected:`, sock.id);
    });
    sock.on("disconnect", () => {
      setConnectionStatus("Disconnected");
    });
    // Aktualizace hráčů + odvození mého currentRoom
    sock.on(GameEvents.PLAYERS_UPDATE, (updatedUsers) => {
      console.log(`[${new Date().toISOString()}] Players update received:`, updatedUsers);
      const currentUsername = usernameRef.current;
      const me = updatedUsers.find(u => u.name === currentUsername);
      const myRoomId = me && me.roomId ? me.roomId : null;
      if (myRoomId !== currentRoom) {
        console.log(`[${new Date().toISOString()}] Derived currentRoom:`, myRoomId);
        setCurrentRoom(myRoomId);
      }
      if (myRoomId) {
        const filtered = updatedUsers.filter(u => u.roomId === myRoomId && u.name !== currentUsername);
        setPlayers(filtered);
      } else {
        const filtered = updatedUsers.filter(u => u.name !== currentUsername);
        setPlayers(filtered);
      }
    });

    return () => sock.close();
  }, []); // Keep empty dependency array to prevent socket recreation

  const handleLogout = () => {
    sessionStorage.removeItem("username");
    setUsername("");
    window.location.href = "/whoami";
  };
  const handleGarage = () => {
    // Use navigate instead of window.location.href to prevent socket disconnection
    window.location.href = "/garage";
  };

  const handleHome = () => {
    // Use navigate instead of window.location.href to prevent socket disconnection
    window.location.href = "/";
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  return (
    <Router>
      <AppContent 
        socket={socket}
        username={username}
        setUsername={setUsername}
        selectedSkin={selectedSkin}
        setSelectedSkin={setSelectedSkin}
        players={players}
        theme={theme}
        toggleTheme={toggleTheme}
      />
    </Router>
  );
}

// Inner component that has access to Router context
function AppContent({ socket, username, setUsername, selectedSkin, setSelectedSkin, players, theme, toggleTheme }) {
  const navigate = useNavigate();

  const handleLogout = () => {
    sessionStorage.removeItem("username");
    setUsername("");
    navigate("/whoami");
  };
  
  const handleGarage = () => {
    navigate("/garage");
  };

  const handleHome = () => {
    navigate("/");
  };

  return (
    <div className={`app-container theme-${theme}`}>
      <div className="background-overlay"></div>
      <Routes>
        <Route
          path="/whoami"
          element={<WhoAmIPage setUsername={setUsername} />}
        />
        <Route
          path="/garage"
          element={
            !username ? <Navigate to="/whoami" /> :
              <PublicGaragePage
                socket={socket}
                username={username}
                selectedSkin={selectedSkin}
                setSelectedSkin={setSelectedSkin}
              />
          }
        />
        <Route
          path="/"
          element={
            !username ? (
              <Navigate to="/whoami" />
            ) : (
              <MainPageWithGarageButton
                socket={socket}
                username={username}
                selectedSkin={selectedSkin}
                setSelectedSkin={setSelectedSkin}
                setUsername={setUsername}
                theme={theme}
                toggleTheme={toggleTheme}
              />
            )
          }
        />
        <Route 
          path="/game/:roomId" 
          element={
            !username ? <Navigate to="/whoami" /> :
              <Game3DPageWrapper 
                username={username} 
                selectedSkin={selectedSkin} 
                socket={socket}
              />
          } 
        />
        <Route 
          path="/garage/:roomId" 
          element={
            !username ? <Navigate to="/whoami" /> :
              <RoomGarageWrapper 
                socket={socket} 
                username={username} 
                selectedSkin={selectedSkin} 
                setSelectedSkin={setSelectedSkin} 
              />
          } 
        />
        <Route 
          path="/room/:roomId/garage" 
          element={
            !username ? <Navigate to="/whoami" /> :
              <RoomGarageWrapper 
                socket={socket} 
                username={username} 
                selectedSkin={selectedSkin} 
                setSelectedSkin={setSelectedSkin} 
              />
          } 
        />

      </Routes>
    </div>
  );
}

export default App;

// User Info Component with Icons
function UserInfoPanel({ socket, username, onLogout }) {
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('Disconnected');

  useEffect(() => {
    if (!socket) return;

    // Set connection status
    setConnectionStatus(socket.connected ? 'Connected' : 'Disconnected');

    // Listen for connection changes
    const handleConnect = () => setConnectionStatus('Connected');
    const handleDisconnect = () => setConnectionStatus('Disconnected');

    socket.on('connect', handleConnect);
    socket.on('disconnect', handleDisconnect);

    // Request initial data
    socket.emit(GameEvents.PLAYERS_REQUEST);
    socket.emit(GameEvents.ROOMS_REQUEST);

    socket.on(GameEvents.PLAYERS_UPDATE, (updatedUsers) => {
      setUsers(updatedUsers);
    });

    socket.on(GameEvents.ROOMS_UPDATE, (updatedRooms) => {
      setRooms(updatedRooms);
    });

    return () => {
      socket.off('connect', handleConnect);
      socket.off('disconnect', handleDisconnect);
      socket.off(GameEvents.PLAYERS_UPDATE);
      socket.off(GameEvents.ROOMS_UPDATE);
    };
  }, [socket]);

  const handleLogout = () => {
    if (onLogout) {
      onLogout();
    }
  };

  return (
    <div className="user-info-panel">
      <div className="user-info-left">
        <div className="user-info-item">
          <span className="user-info-icon">👤</span>
          <span className="user-info-text">{username}</span>
        </div>
        <div className="user-info-item">
          <span className="user-info-icon">👥</span>
          <span className="user-info-text">{users.length}</span>
        </div>
        <div className="user-info-item">
          <span className="user-info-icon">🏠</span>
          <span className="user-info-text">{rooms.length}</span>
        </div>
      </div>
      <div className="user-info-right">
        <div className="user-info-item">
          <span className={`user-info-icon ${connectionStatus.toLowerCase()}`}>
            {connectionStatus === 'Connected' ? '🟢' : '🔴'}
          </span>
          <span className="user-info-text">{connectionStatus}</span>
        </div>
        <button 
          className="logout-button"
          onClick={handleLogout}
          title="Logout"
        >
          <span className="user-info-icon">🚪</span>
        </button>
      </div>
    </div>
  );
}

// Přidám komponentu pro hlavní stránku s tlačítkem do garáže
function MainPageWithGarageButton({ socket, username, selectedSkin, setSelectedSkin, setUsername, theme, toggleTheme }) {
  const navigate = useNavigate();
  
  const handleLogout = () => {
    sessionStorage.removeItem("username");
    setUsername("");
    navigate("/whoami");
  };

  const handleGarage = () => {
    navigate("/garage");
  };

  const handleJoinGame = (roomId) => {
    navigate(`/room/${roomId}/garage`);
  };

  return (
    <div className="content">
      <div className="panel">
        <ThemeToggleElement theme={theme} toggleTheme={toggleTheme} />
        <UserInfoPanel socket={socket} username={username} onLogout={handleLogout} />
        <Chat socket={socket} username={username} />
      </div>
      <RoomPanel socket={socket} username={username} onGarage={handleGarage} onJoinGame={handleJoinGame} />
    </div>
  );
}
